import {Link, useParams} from 'react-router-dom';
import {useAuth} from '../../providers/AuthProvider';
import LoadingSpinner from '../../components/LoadingSpinner';

import {TalentOppQuickSummary} from '../talent-opportunities/OppQuickSummary';
import React, {useMemo} from "react";
import ErrorBanner from "../../components/ErrorBanner";
import ResourceNotFound from "../../components/errors/ResourceNotFound";
import {useCreateAgencyOpportunityMutation} from "../../models/AgencyOpportunity";
import CircleProgress from "../../components/CircleProgress";
import {ProgressBar} from "react-step-progress-bar";
import UserImageCircle from "../../components/UserImageCircle";
import {useAPI} from "../../config/API";
import {fetchJobBoardDetails} from "../../requests/TalentUserRequests";
import {JobDescription} from "../talent-job-board/JobDetails";
import NEWSkillFormButton from '../profiles/components/NEWSkillFormButton';

const SupplierJobBoardDetails = (props) => {
    const auth = useAuth();
    const { jobId } = useParams();

    const {
        data: job,
        isFetching,
        isLoading,
        isRefetching,
        error,
        refetch,
        ...result
    } = useAPI(['fetchJobBoardDetails', jobId])

    const createAgencyOpportunity = useCreateAgencyOpportunityMutation();


    const matchedTalent = useMemo(() => {
        if (isLoading || !job) return [];

        return TB.orderBy(job.matching_talent || [], 'matching.overall.percent', 'desc');
    }, [isLoading, job]);


    if (isLoading) {
        return (
            <div className="w-full job-match-loading">
                <LoadingSpinner size={'lg'} />
            </div>
        )
    }


    if (!isLoading && error) {
        return (
            <div className="container flex flex-col flex-grow items-center py-8">
                <ErrorBanner onRetry={refetch} />
            </div>
        )
    }

    if (!isLoading && !error && !job) {
        return <ResourceNotFound type="Job" exitLink={'./../../'} exitText="Back to Job Board" />
    }

    return (
        <div className="supplier-job-board-details job-details-page-container container">
            <Link
                to={'./../../'}
                className="btn btn-link btn-link-dark">
                <i className="fas fa-arrow-left" />&nbsp;{props.location?.state?.label || 'Back to Job Board'}
            </Link>

            <div className="job-details-page row flex-col sm:flex-row-reverse md:flex-row">

                <div className="col sm:w-full md:w-9/12">
                    <div className="job-details-candidate-score">

                        <JobDescription job={job}/>

                        <hr />

                        <div>
                            <p className="text-base font-semibold mb-1.5">Matched Talent Resources</p>
                            <div className="candidate-scoring">
                                {matchedTalent?.length > 0 ?
                                    TB.map(matchedTalent, (talent, ind) => (
                                        <div className="pb-4 flex flex-col items-stretch" key={`job-${job.id}-talent-${talent.id}`}>
                                            <div className="flex flex-row items-center gap-3">
                                                <div className="flex flex-row items-center gap-3 flex-1">

                                                    <UserImageCircle size={60} user={talent} className="img-fluid" />
                                                    <div className="flex flex-col gap-0.5">
                                                        <p>{talent.first_name} {talent.last_name}</p>
                                                        {/*<p className="text-xs">{talent.years_of_experience} years of experience</p>*/}
                                                        <p className="text-xs">
                                                            <span className="text-wripplePrimaryDarkNavy font-semibold">Matched {TB.pluralize('Role', talent.matching.disciplines.disciplines?.length)}:</span>
                                                            &nbsp;{TB.map(talent.matching.disciplines.disciplines || [], (dis, i) => (
                                                            <React.Fragment key={`${talent.id}-${dis.id}-${job.id}`}>
                                                                <span className="text-primary font-semibold">
                                                                    {TB.find(job.disciplines, ['id', dis.discipline_id]).name}
                                                                </span>
                                                                {i < talent.matching.disciplines.disciplines?.length - 1 && ', '}
                                                            </React.Fragment>
                                                        ))}
                                                        </p>
                                                    </div>

                                                </div>

                                                <div style={{ flex: 2 }}>
                                                    <div className="flex pb-1">
                                                        <div className="flex-1 flex justify-center items-center">
                                                            <p className="p-0 pr-2 text-xs font-semibold text-wrippleBlueGray">Skills <br /> Expertise</p>

                                                            <CircleProgress
                                                                progress={talent.matching.skills.overall.percent}
                                                                strokeWidth={8}
                                                                size={85}
                                                            >
                                                                <p className="text-wrippleBlueGray">
                                                                    <span className="text-sm">{Math.round(talent.matching.skills.overall.percent)}%</span>
                                                                    <small className="text-xxs">Match</small>
                                                                </p>
                                                            </CircleProgress>
                                                        </div>

                                                        <div className="flex-1 flex justify-center items-center">
                                                            <p className="p-0 pr-2 text-xs font-semibold text-wrippleBlueGray">Years <br /> Experience</p>

                                                            <CircleProgress
                                                                progress={talent.matching.experience.percent}
                                                                strokeWidth={8}
                                                                size={85}
                                                            >
                                                                <p className="text-wrippleBlueGray">
                                                                    <span className="text-sm">{Math.round(talent.matching.experience.percent)}%</span>
                                                                    <small className="text-xxs">Match</small>
                                                                </p>
                                                            </CircleProgress>
                                                        </div>

                                                        <div className="flex-1 flex justify-center items-center">
                                                            <p className="p-0 pr-2 text-xs font-semibold text-wrippleBlueGray">Industry <br /> Experience</p>

                                                            <CircleProgress
                                                                progress={talent.matching.industries.overall.percent}
                                                                strokeWidth={8}
                                                                size={85}
                                                            >
                                                                <p className="text-wrippleBlueGray">
                                                                    <span className="text-sm">{Math.round(talent.matching.industries.overall.percent)}%</span>
                                                                    <small className="text-xxs">Match</small>
                                                                </p>
                                                            </CircleProgress>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>
                                            <div className="flex-row pt-1 pb-4">
                                                <div className="flex-col max-w-[380px] ml-auto">
                                                    <p>{Math.round(talent.matching.overall.percent)}% total match</p>
                                                    <ProgressBar
                                                        filledBackground={TB.config('color.orange')}
                                                        height={13}
                                                        percent={talent.matching.overall.percent}
                                                        unfilledBackground={TB.config('color.gray')}
                                                    />
                                                </div>
                                            </div>
                                            {ind !== matchedTalent.length - 1 && <div className="border-b border-solid w-3/4 m-auto"></div>}
                                        </div>
                                    )) : (
                                        <div className="job-match-score-panel no-match">

                                            <p className="pt-3 no-match-header">
                                                You don't have any talent resources matched for this job. Talent resources must have
                                                one the related Wripple roles to be considered a match.
                                            </p>

                                            <Link to={'/supplier/manage-profile/talent-resources'}
                                                  className="btn btn-primary no-match-btn">
                                                Manage Talent Members
                                            </Link>

                                        </div>
                                    )}
                            </div>
                        </div>

                        <hr/>

                        <div className="flex flex-col gap-y-4 job-match-scores-container">

                            <div>
                                <p className="text-base pb-2 font-semibold">Skill Expertise</p>

                                <div className="discipline-skills-container skill-importance-container gap-3">
                                    {job.skills.map(skill => {
                                        return (
                                            <NEWSkillFormButton
                                                key={skill.name}
                                                className="pointer-events-none"
                                                valuePropName="importance"
                                                scoringOptions={laravel.importance_levels}
                                                skill={skill}
                                                disabled
                                                readonly
                                            />
                                        )
                                    })}
                                </div>
                            </div>
                            <div>
                                <p className="text-base pb-2 font-semibold">Industry Expertise</p>

                                <div className="discipline-skills-container skill-importance-container gap-3 pointer-events-none">
                                    {job.industries.length ? job.industries.map(industry => {
                                        return (
                                            <NEWSkillFormButton
                                                key={industry.name}
                                                className="pointer-events-none"
                                                valuePropName="importance"
                                                scoringOptions={laravel.importance_levels}
                                                skill={industry}
                                                disabled
                                                readonly
                                            />
                                        )
                                    }) : (
                                        <p className="italic text-xs">
                                            N/A
                                        </p>
                                    )}
                                </div>
                            </div>

                        </div>

                    </div>
                </div>


                <div className="col sm:w-full md:w-3/12">
                    <TalentOppQuickSummary
                        job={job}
                        isDisabled={createAgencyOpportunity.isLoading}
                        onToggleInterest={() => createAgencyOpportunity.mutate(job)}
                    />
                </div>

            </div>
        </div>
    )
}

export default SupplierJobBoardDetails;